import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CardFront, CardBack, cardRadiusPx } from "../components/AadhaarCard/AadhaarCard";
import RealisticStage from "../components/AadhaarCard/RealisticStage";
import { SURFACES } from "../components/AadhaarCard/surfaces";
import { EFFECT_STYLES } from "../components/AadhaarCard/effects";
import useElementWidth from "../hooks/useElementWidth";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useCard } from "../context/CardContext";
import api from "../api/client";
import { downloadCardPdf, downloadCardPng, downloadRealisticCardPng } from "../utils/pdf";
import { groupDigits, formatDob } from "../utils/fictional";
import Icon from "../components/ui/icons";
import useAntiInspect from "../hooks/useAntiInspect";
import "./generator.css";

/** Export width: 10px per mm of an 85.6mm card. */
const EXPORT_WIDTH = 856;

/** Preview ceiling. Deliberately well under the export size - this is a proof,
    not the artefact, and a smaller card leaves room for the control rail. The
    card never exceeds its container, so a phone gets a card that fits rather
    than a page that scrolls sideways. */
const PREVIEW_MAX = 380;

const SIDES = [
  { key: "front", label: "Front" },
  { key: "back", label: "Back" },
  { key: "both", label: "Both" },
];

/** Overlay drawn on top of the blurred preview. */
function Veil() {
  return (
    <div className="ac-card-veil" aria-hidden="true">
      <Icon.Lock />
      <span>Hidden from screenshots</span>
    </div>
  );
}

/** Marks every on-screen preview as a preview - present regardless of the
    screenshot-blur state, absent from the export stage below (see
    .ac-card-watermark in App.css) so a generated PDF/PNG stays clean. */
function Watermark() {
  return <div className="ac-card-watermark" aria-hidden="true" />;
}

export default function GeneratorPage() {
  const { isAuthed, entitlements, refreshProfile } = useAuth();
  const toast = useToast();
  const {
    data,
    lang,
    qr,
    hasErrors,
    form,
    seed,
    regenerate,
    shuffleName,
    contributeToPool,
  } = useCard();

  const [busy, setBusy] = useState("");
  const [side, setSide] = useState("both");
  const [shielded, setShielded] = useState(true);
  const [unlocked, setUnlocked] = useState(null);
  const [realistic, setRealistic] = useState(false);
  const [surfaceKey, setSurfaceKey] = useState(
    () => localStorage.getItem("foliox-realistic-surface") || "default"
  );
  const [effectKey, setEffectKey] = useState(
    () => localStorage.getItem("foliox-realistic-effect") || "natural"
  );
  const [pixelBlur, setPixelBlur] = useState(
    () => localStorage.getItem("foliox-pixel-blur") === "true"
  );
  const [damageLevel, setDamageLevel] = useState(
    () => localStorage.getItem("foliox-damage-level") || "none"
  );
  const [bgBlur, setBgBlur] = useState(
    () => parseInt(localStorage.getItem("foliox-bg-blur") || "40")
  );
  // Defaults on: the grounding shadow is what makes the card read as resting
  // on the surface rather than floating over it.
  const [groundShadow, setGroundShadow] = useState(
    () => localStorage.getItem("foliox-ground-shadow") !== "false"
  );

  useEffect(() => {
    try {
      localStorage.setItem("foliox-ground-shadow", groundShadow);
    } catch (err) {
      /* private mode or a full quota; the choice just is not remembered */
    }
  }, [groundShadow]);

  useEffect(() => {
    try {
      localStorage.setItem("foliox-realistic-surface", surfaceKey);
    } catch (err) {
      /* private mode or a full quota; the choice just is not remembered */
    }
  }, [surfaceKey]);

  useEffect(() => {
    try {
      localStorage.setItem("foliox-realistic-effect", effectKey);
    } catch (err) {
      /* private mode or a full quota; the choice just is not remembered */
    }
  }, [effectKey]);

  useEffect(() => {
    try {
      localStorage.setItem("foliox-pixel-blur", pixelBlur);
    } catch (err) {
      /* private mode or a full quota */
    }
  }, [pixelBlur]);

  useEffect(() => {
    try {
      localStorage.setItem("foliox-damage-level", damageLevel);
    } catch (err) {
      /* private mode or a full quota */
    }
  }, [damageLevel]);

  useEffect(() => {
    try {
      localStorage.setItem("foliox-bg-blur", bgBlur);
    } catch (err) {
      /* private mode or a full quota */
    }
  }, [bgBlur]);

  const exportFrontRef = useRef(null);
  const exportBackRef = useRef(null);
  const [stageRef, stageWidth] = useElementWidth(PREVIEW_MAX);

  // The preview re-blurs itself the moment DevTools looks open or this
  // window loses focus - covers switching to a screenshot tool, but not a
  // capture taken without ever leaving this window (nothing client-side can
  // catch that; see useAntiInspect).
  const devtoolsOpen = useAntiInspect();
  useEffect(() => {
    if (devtoolsOpen) setShielded(true);
  }, [devtoolsOpen]);
  useEffect(() => {
    const onBlur = () => setShielded(true);
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, []);

  // Two cards sit side by side once there is room for both plus the gap.
  const previewWidth = Math.max(
    220,
    Math.min(PREVIEW_MAX, side === "both" && stageWidth > 820
      ? (stageWidth - 30) / 2
      : stageWidth)
  );

  // The Realistic look is driven by these five choices. The exports take the
  // same object the preview is rendered from, so a download reflects whatever
  // is on screen instead of falling back to the bare card.
  const sceneOpts = useMemo(
    () => ({
      surfaceKey,
      effectKey,
      pixelBlur,
      damageLevel,
      bgBlur,
      groundShadow,
      cornerRadius: data.cornerRadius,
    }),
    [surfaceKey, effectKey, pixelBlur, damageLevel, bgBlur, groundShadow, data.cornerRadius]
  );

  // The corner in px at preview size, for the boxes wrapping the card.
  const previewRadius = cardRadiusPx(previewWidth, data.cornerRadius);

  // Any edit invalidates the card that was paid for, so one generation buys
  // one card rather than an open-ended session.
  useEffect(() => {
    setUnlocked(null);
  }, [form, seed]);

  const handleGenerate = useCallback(async () => {
    setBusy("gen");
    try {
      const res = await api.post("/api/generations", {
        card: {
          state: form.state,
          name: data.name,
          nameLocal: data.nameLocal,
          dob: data.dob,
          gender: data.gender,
          address: data.address,
          addressLocal: data.addressLocal,
          aadhaar: data.aadhaar,
          vid: data.vid,
          photo: data.photo || null,
        },
      });
      setUnlocked(res.generation);
      await refreshProfile();
      contributeToPool();
      toast.ok(
        res.spent.source === "plan"
          ? "Generated. Exports unlocked for this card."
          : `Generated. ${res.spent.creditsLeft} credit(s) left.`
      );
    } catch (err) {
      toast.err(err.message || "Could not generate.");
    } finally {
      setBusy("");
    }
  }, [form.state, data, refreshProfile, toast, contributeToPool]);

  const runExport = useCallback(
    async (key, label, task) => {
      setBusy(key);
      try {
        await task();
        toast.ok(`${label} downloaded`);
      } catch (err) {
        toast.err(err.message || "Export failed.");
      } finally {
        setBusy("");
      }
    },
    [toast]
  );

  const ent = entitlements;
  const plan = ent?.plan;
  const canExport = !!unlocked && !hasErrors && busy === "";
  const cardProps = { data, lang };

  const allowance =
    ent?.windowLimit === -1
      ? "Unlimited"
      : ent
      ? `${ent.windowRemaining ?? 0} / ${ent.windowLimit}`
      : "—";

  return (
    <>
      <main className="gen">
        <div className="gen-aura" aria-hidden="true" />

        <header className="gen-head">
          <div>
            <span className="gen-eyebrow">Specimen generator</span>
            <h1>
              {data.name || "Untitled card"}
              <span className="gen-ref">{groupDigits(data.aadhaar)}</span>
            </h1>
          </div>

          <Link className="btn btn-primary gen-editlink" to="/settings">
            <Icon.Sliders />
            Card settings
          </Link>
        </header>

        <div className="gen-layout">
          {/* ---------- Stage ---------- */}
          <section className="gen-stage" aria-label="Card preview">
            <div className="gen-stage-bar">
              <div className="tabs" role="tablist" aria-label="Card side">
                {SIDES.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    role="tab"
                    aria-selected={side === item.key}
                    className={side === item.key ? "is-active" : ""}
                    onClick={() => setSide(item.key)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <span className="gen-bar-spacer" />

              <button
                type="button"
                className={`gen-shield${realistic ? " is-on" : ""}`}
                onClick={() => setRealistic((v) => !v)}
                aria-pressed={realistic}
                title="Preview the card as a photo of the printed hardcopy - desk surface, tilt and grain."
              >
                <Icon.Image />
                Realistic
              </button>

              <button
                type="button"
                className={`gen-shield${shielded ? " is-on" : ""}`}
                onClick={() => !devtoolsOpen && setShielded((v) => !v)}
                disabled={devtoolsOpen}
                aria-pressed={shielded}
                title={
                  devtoolsOpen
                    ? "Locked while developer tools appear to be open"
                    : shielded
                    ? "Card data is blurred in the preview"
                    : "Card data is visible in the preview"
                }
              >
                {shielded ? <Icon.Lock /> : <Icon.Eye />}
                {shielded ? "Blurred" : "Visible"}
              </button>
            </div>

            {realistic && (
              <div className="studio">
                <div className="studio-row">
                  <span className="studio-label">Surface</span>
                  <div className="studio-swatches" role="radiogroup" aria-label="Backdrop surface">
                    {SURFACES.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        role="radio"
                        aria-checked={surfaceKey === s.key}
                        aria-label={s.label}
                        className={`studio-swatch${surfaceKey === s.key ? " is-active" : ""}`}
                        style={{ background: s.image ? `url(${s.image}) center/cover` : s.css }}
                        onClick={() => setSurfaceKey(s.key)}
                        title={s.label}
                      >
                        <span className="studio-swatch-name">{s.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="studio-row">
                  <span className="studio-label">Look</span>
                  <div className="studio-pills" role="radiogroup" aria-label="Photo look">
                    {EFFECT_STYLES.map((e) => (
                      <button
                        key={e.key}
                        type="button"
                        role="radio"
                        aria-checked={effectKey === e.key}
                        className={`studio-pill${effectKey === e.key ? " is-active" : ""}`}
                        onClick={() => setEffectKey(e.key)}
                      >
                        {e.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="studio-row">
                  <span className="studio-label">Condition</span>
                  <div className="studio-pills" role="radiogroup" aria-label="Card damage level">
                    {[
                      { key: "none", label: "Perfect" },
                      { key: "light", label: "Light wear" },
                      { key: "medium", label: "Worn" },
                      { key: "heavy", label: "Heavy" },
                    ].map((d) => (
                      <button
                        key={d.key}
                        type="button"
                        role="radio"
                        aria-checked={damageLevel === d.key}
                        className={`studio-pill is-damage${damageLevel === d.key ? " is-active" : ""}`}
                        onClick={() => setDamageLevel(d.key)}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="studio-row studio-row-split">
                  <div className="studio-toggles">
                    <button
                      type="button"
                      className={`studio-switch${groundShadow ? " is-on" : ""}`}
                      onClick={() => setGroundShadow((v) => !v)}
                      aria-pressed={groundShadow}
                      title="Seat the card on the surface with a soft contact shadow"
                    >
                      <span className="studio-switch-track" aria-hidden="true">
                        <span className="studio-switch-knob" />
                      </span>
                      Contact shadow
                    </button>

                    <button
                      type="button"
                      className={`studio-switch${pixelBlur ? " is-on" : ""}`}
                      onClick={() => setPixelBlur((v) => !v)}
                      aria-pressed={pixelBlur}
                      title="Soften the card to obscure fine detail"
                    >
                      <span className="studio-switch-track" aria-hidden="true">
                        <span className="studio-switch-knob" />
                      </span>
                      Soft focus
                    </button>
                  </div>

                  <div className="studio-slider">
                    <label htmlFor="bg-blur-slider">
                      Depth of field
                      <em>{bgBlur}%</em>
                    </label>
                    <input
                      id="bg-blur-slider"
                      type="range"
                      min="0"
                      max="100"
                      value={bgBlur}
                      onChange={(e) => setBgBlur(parseInt(e.target.value))}
                      title="How far out of focus the backdrop falls"
                    />
                  </div>
                </div>
              </div>
            )}

            <div
              className={`gen-cards${shielded ? " is-shielded" : ""}`}
              ref={stageRef}
            >
              {side !== "back" && (
                <figure className="gen-card">
                  {realistic ? (
                    <RealisticStage
                      card={<CardFront {...cardProps} width={previewWidth} />}
                      veil={<Veil />}
                      tilt={-2}
                      surfaceKey={surfaceKey}
                      effectKey={effectKey}
                      pixelBlur={pixelBlur}
                      damageLevel={damageLevel}
                      bgBlur={bgBlur}
                      cardRadius={previewRadius}
                      groundShadow={groundShadow}
                    />
                  ) : (
                    <div
                      className="ac-card-shield"
                      style={{ "--card-radius": `${previewRadius}px` }}
                    >
                      <CardFront {...cardProps} width={previewWidth} />
                      <Watermark />
                      <Veil />
                    </div>
                  )}
                  <figcaption>Front</figcaption>
                </figure>
              )}
              {side !== "front" && (
                <figure className="gen-card">
                  {realistic ? (
                    <RealisticStage
                      card={<CardBack {...cardProps} qr={qr} width={previewWidth} />}
                      veil={<Veil />}
                      tilt={1.5}
                      surfaceKey={surfaceKey}
                      effectKey={effectKey}
                      pixelBlur={pixelBlur}
                      damageLevel={damageLevel}
                      bgBlur={bgBlur}
                      cardRadius={previewRadius}
                      groundShadow={groundShadow}
                    />
                  ) : (
                    <div
                      className="ac-card-shield"
                      style={{ "--card-radius": `${previewRadius}px` }}
                    >
                      <CardBack {...cardProps} qr={qr} width={previewWidth} />
                      <Watermark />
                      <Veil />
                    </div>
                  )}
                  <figcaption>Back</figcaption>
                </figure>
              )}
            </div>

            <div className="gen-quick">
              <button type="button" className="btn btn-sm" onClick={regenerate}>
                <Icon.Refresh />
                Re-roll identity
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={async () => {
                  if (!(await shuffleName())) toast.err("Name pool is empty.");
                }}
              >
                <Icon.Spark />
                Shuffle name
              </button>
              <Link className="btn btn-sm" to="/settings">
                Edit details
              </Link>
            </div>
          </section>

          {/* ---------- Rail ---------- */}
          <aside className="gen-rail">
            <div className="rail-card rail-status">
              <div className="rail-status-top">
                <span className="rail-label">Allowance</span>
                <strong>{allowance}</strong>
              </div>
              <div className="rail-meter" aria-hidden="true">
                <span
                  style={{
                    width:
                      ent && ent.windowLimit > 0
                        ? `${Math.min(100, (ent.windowUsed / ent.windowLimit) * 100)}%`
                        : ent?.windowLimit === -1
                        ? "100%"
                        : "0%",
                  }}
                />
              </div>
              <p className="rail-sub">
                {!isAuthed
                  ? "Preview is free. Sign in to generate."
                  : plan
                  ? `${plan.planName} plan${
                      ent.credits ? ` · ${ent.credits} top-up credits` : ""
                    }`
                  : "No plan"}
              </p>
            </div>

            <div className="rail-card">
              <span className="rail-label">Export</span>

              {!unlocked ? (
                <>
                  <button
                    type="button"
                    className="btn btn-primary rail-cta"
                    disabled={hasErrors || busy !== "" || !isAuthed || !ent?.canGenerate}
                    onClick={handleGenerate}
                  >
                    {busy === "gen" ? (
                      <span className="spinner" aria-hidden="true" />
                    ) : (
                      <Icon.Download />
                    )}
                    {busy === "gen" ? "Generating…" : "Generate card"}
                  </button>

                  {!isAuthed && (
                    <p className="rail-note">
                      <Link to="/login">Sign in</Link> to generate this card.
                    </p>
                  )}
                  {isAuthed && ent && !ent.canGenerate && (
                    <p className="rail-note is-warn">
                      {plan?.planCode === "free"
                        ? "You need a plan or top-up credits."
                        : `6-hour limit reached (${ent.windowUsed}/${ent.windowLimit}).`}{" "}
                      <Link to="/pricing">See plans</Link>
                    </p>
                  )}
                  {isAuthed && ent?.canGenerate && (
                    <p className="rail-note">
                      Costs one {ent.nextSource === "credit" ? "credit" : "generation"}.
                      Exports are then free for this card.
                    </p>
                  )}
                  {hasErrors && (
                    <p className="rail-note is-warn">
                      Fix the highlighted fields in{" "}
                      <Link to="/settings">card settings</Link>.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-primary rail-cta"
                    disabled={!canExport}
                    onClick={() =>
                      runExport("pdf", "PDF", () =>
                        downloadCardPdf(
                          exportFrontRef.current,
                          exportBackRef.current,
                          "specimen-aadhaar-card.pdf",
                          realistic ? sceneOpts : null
                        )
                      )
                    }
                  >
                    {busy === "pdf" ? (
                      <span className="spinner" aria-hidden="true" />
                    ) : (
                      <Icon.Download />
                    )}
                    {busy === "pdf" ? "Rendering…" : "Download PDF"}
                  </button>

                  <div className="rail-split">
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={!canExport}
                      onClick={() =>
                        runExport("png-f", "Front PNG", () =>
                          realistic
                            ? downloadRealisticCardPng(
                                exportFrontRef.current,
                                "realistic-front.png",
                                { ...sceneOpts, tilt: -2 }
                              )
                            : downloadCardPng(exportFrontRef.current, "specimen-front.png")
                        )
                      }
                    >
                      {realistic ? "Realistic PNG" : "PNG"} front
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={!canExport}
                      onClick={() =>
                        runExport("png-b", "Back PNG", () =>
                          realistic
                            ? downloadRealisticCardPng(
                                exportBackRef.current,
                                "realistic-back.png",
                                { ...sceneOpts, tilt: 1.5 }
                              )
                            : downloadCardPng(exportBackRef.current, "specimen-back.png")
                        )
                      }
                    >
                      {realistic ? "Realistic PNG" : "PNG"} back
                    </button>
                  </div>

                  <p className="rail-note is-ok">
                    Unlocked. <Link to="/history">History</Link> keeps it for an hour.
                  </p>
                </>
              )}
            </div>

            <div className="rail-card">
              <span className="rail-label">This card</span>
              <dl className="rail-facts">
                <div>
                  <dt>Name</dt>
                  <dd>{data.name || "—"}</dd>
                </div>
                <div>
                  <dt>Local</dt>
                  <dd className={`ac-script-${lang.script}`}>{data.nameLocal || "—"}</dd>
                </div>
                <div>
                  <dt>Born</dt>
                  <dd>{formatDob(data.dob) || "—"}</dd>
                </div>
                <div>
                  <dt>Gender</dt>
                  <dd className="cap">{data.gender || "—"}</dd>
                </div>
                <div>
                  <dt>Reference</dt>
                  <dd className="mono">{groupDigits(data.aadhaar)}</dd>
                </div>
                <div>
                  <dt>VID</dt>
                  <dd className="mono">{groupDigits(data.vid)}</dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      </main>

      {/* Fixed-size off-screen copies: exports stay identical regardless of the
          viewport the preview happens to be at. */}
      <div className="export-stage" aria-hidden="true">
        <CardFront {...cardProps} width={EXPORT_WIDTH} ref={exportFrontRef} />
        <CardBack {...cardProps} qr={qr} width={EXPORT_WIDTH} ref={exportBackRef} />
      </div>
    </>
  );
}
